// Function index:
//   BannerHandle           — controller interface returned by mountCombatBanner.
//   mountCombatBanner()    — wire the banner DOM nodes; returns BannerHandle.
//
// Combat banner. Shown only when a combat is active for the current party.
// Tracks who's up, the round number, and everyone's HP in initiative order.
//
// Wired to outbound events:
//   combat.started        — full snapshot replaces banner state
//   combat.ended          — banner hides
//   combat.turn           — advances active highlight + round counter
//   character.hp_changed  — updates the matching participant's HP/defeated
//   npc.hp_changed        — same, by npc_id

import type { CombatParticipant, CombatSnapshot } from './api.js';

export interface BannerHandle {
  // Reset to a fresh snapshot (or null to hide the banner).
  setCombat(snapshot: CombatSnapshot | null): void;
  // Advance the active participant + round.
  setTurn(round_num: number, current_turn_idx: number): void;
  // Update a character's HP. defeated is derived (hp === 0).
  updateCharacterHp(character_id: number, hp: number, max_hp: number): void;
  updateNpcHp(npc_id: number, hp: number, max_hp: number, defeated: boolean): void;
  // Whose turn it is (active participant). null when no active combat.
  active(): CombatParticipant | null;
}

export function mountCombatBanner(): BannerHandle {
  const bar     = document.getElementById('combat-bar') as HTMLDivElement;
  const roundEl = document.getElementById('combat-round') as HTMLSpanElement;
  const activeEl = document.getElementById('combat-active') as HTMLSpanElement;
  const chipsEl = document.getElementById('combat-participants') as HTMLSpanElement;

  let combat: CombatSnapshot | null = null;

  function render(): void {
    if (!combat) { bar.hidden = true; return; }
    bar.hidden = false;
    roundEl.textContent = `Round ${combat.round_num}`;
    const active = combat.participants[combat.current_turn_idx];
    activeEl.textContent = active ? `· ${active.name}'s turn` : '';
    chipsEl.innerHTML = '';
    combat.participants.forEach((p, idx) => {
      const chip = document.createElement('span');
      const classes = ['combat-chip', p.side];
      if (idx === combat!.current_turn_idx) classes.push('active');
      if (p.defeated) classes.push('defeated');
      chip.className = classes.join(' ');
      chip.innerHTML = `${escape(p.name)} <span class="hp">${p.hp}/${p.max_hp}</span>`;
      chipsEl.appendChild(chip);
    });
  }

  return {
    setCombat(snapshot) {
      combat = snapshot ? cloneSnapshot(snapshot) : null;
      render();
    },
    setTurn(round_num, current_turn_idx) {
      if (!combat) return;
      combat.round_num = round_num;
      combat.current_turn_idx = current_turn_idx;
      render();
    },
    updateCharacterHp(character_id, hp, max_hp) {
      if (!combat) return;
      for (const p of combat.participants) {
        if (p.kind === 'character' && p.character_id === character_id) {
          p.hp = hp; p.max_hp = max_hp; p.defeated = hp <= 0;
        }
      }
      render();
    },
    updateNpcHp(npc_id, hp, max_hp, defeated) {
      if (!combat) return;
      for (const p of combat.participants) {
        if (p.kind === 'npc' && p.npc_id === npc_id) {
          p.hp = hp; p.max_hp = max_hp; p.defeated = defeated || hp <= 0;
        }
      }
      render();
    },
    active() {
      return combat?.participants[combat.current_turn_idx] ?? null;
    },
  };
}

function cloneSnapshot(s: CombatSnapshot): CombatSnapshot {
  return { ...s, participants: s.participants.map((p) => ({ ...p })) };
}

function escape(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}
